import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const rawTheme = createTheme({
    palette: {
        mode: 'light',
        common: {
            black: '#000000',
            white: '#FFFFFF',
        },
        primary: {
            main: '#003366',
            light: '#0055A4',
            dark: '#002244',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#00CFFE',
            light: '#66EDFF',
            dark: '#0090B3',
            contrastText: '#003366',
        },
        success: {
            main: '#28a745',
            light: '#5cb85c',
            dark: '#1c7430',
            contrastText: '#FFFFFF',
        },
        error: {
            main: '#dc3545',
            light: '#e4606d',
            dark: '#a71d2a',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#ffc107',
            light: '#ffd54f',
            dark: '#c79100',
            contrastText: '#212529',
        },
        info: {
            main: '#17a2b8',
            light: '#5bc0de',
            dark: '#117a8b',
            contrastText: '#FFFFFF',
        },
        grey: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#eeeeee',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
        },
        background: {
            default: '#f3f6f9',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#003366',
            secondary: '#0055A4',
            disabled: 'rgba(0, 51, 102, 0.38)',
        },
        action: {
            active: '#0055A4',
            hover: 'rgba(0, 51, 102, 0.04)',
            selected: 'rgba(0, 51, 102, 0.08)',
            disabled: 'rgba(0, 51, 102, 0.26)',
            disabledBackground: 'rgba(0, 51, 102, 0.12)',
            focus: 'rgba(0, 51, 102, 0.12)',
        },
    },
    typography: {
        fontFamily: [
            'Red Hat Text',
            'Cabin',
            'Red Hat Display',
            'Roboto',
            'Helvetica',
            'Arial',
            'sans-serif'
        ].join(','),
        fontSize: 16,
        htmlFontSize: 17,
        fontWeightLight: 400,
        fontWeightRegular: 500,
        fontWeightMedium: 600,
        fontWeightBold: 700,
        h1: {
            fontFamily: '"Red Hat Display", "Cabin", Arial, sans-serif',
            fontWeight: 900,
            fontSize: '3.5rem',
            lineHeight: 1.12,
            letterSpacing: '-0.03em',
        },
        h2: {
            fontFamily: '"Red Hat Display", "Cabin", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '2.7rem',
            lineHeight: 1.18,
            letterSpacing: '-0.02em',
        },
        h3: {
            fontFamily: '"Cabin", "Red Hat Display", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '2.1rem',
            lineHeight: 1.25,
        },
        h4: {
            fontFamily: '"Cabin", "Red Hat Display", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '1.7rem',
            lineHeight: 1.32,
        },
        h5: {
            fontFamily: '"Cabin", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '1.3rem',
            lineHeight: 1.45,
        },
        h6: {
            fontFamily: '"Cabin", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '1.1rem',
            lineHeight: 1.6,
        },
        subtitle1: {
            fontFamily: '"Red Hat Text", Arial, sans-serif',
            fontWeight: 500,
            fontSize: '1.07rem',
            lineHeight: 1.72,
        },
        subtitle2: {
            fontFamily: '"Red Hat Text", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '0.95rem',
            lineHeight: 1.64,
        },
        body1: {
            fontFamily: '"Red Hat Text", Arial, sans-serif',
            fontWeight: 400,
            fontSize: '1.08rem',
            lineHeight: 1.63,
        },
        body2: {
            fontFamily: '"Red Hat Text", Arial, sans-serif',
            fontWeight: 400,
            fontSize: '0.97rem',
            lineHeight: 1.58,
        },
        button: {
            fontFamily: '"Cabin", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '1.02rem',
            textTransform: 'none',
        },
        caption: {
            fontFamily: '"Red Hat Text", Arial, sans-serif',
            fontWeight: 500,
            fontSize: '0.83rem',
            lineHeight: 1.52,
        },
        overline: {
            fontFamily: '"Red Hat Text", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '0.83rem',
            textTransform: 'uppercase',
            letterSpacing: '0.11em',
        },
    },
    shape: {
        borderRadius: 8,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    spacing: 8,
    transitions: {
        easing: {
            easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
            easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
            easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
            sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        },
        duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
        },
    },
    mixins: {
        toolbar: {
            minHeight: 64,
            '@media (min-width:0px)': {
                minHeight: 56,
            },
            '@media (min-width:600px)': {
                minHeight: 64,
            },
        },
    },
    shadows: [
        'none',
        '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
        '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
        '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
        '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
        '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
        '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
        '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
        '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
        '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
        '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
        '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
        '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
        '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
        '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
        '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
        '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
        '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
        '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
        '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
        '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
    ],
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: '#f3f6f9',
                    color: '#003366',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                '.glow': {
                    filter: 'drop-shadow(0 0 6px rgba(0,51,102,0.6))',
                },
                '.haptic-press': {
                    transform: 'scale(0.98)',
                    transition: 'transform 50ms ease-in-out',
                },
                '.haptic-release': {
                    transform: 'scale(1)',
                    transition: 'transform 100ms ease-out',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: false,
                disableRipple: false,
            },
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    transition: 'all 200ms ease',
                    '&:active': {
                        transform: 'scale(0.98)',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #003366 0%, #0055A4 100%)',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 10px rgba(0,51,102,0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #002244 0%, #004080 100%)',
                    },
                },
                outlinedPrimary: {
                    borderColor: '#003366',
                    color: '#003366',
                    '&:hover': {
                        backgroundColor: 'rgba(0,51,102,0.04)',
                    },
                },
                textPrimary: {
                    color: '#003366',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: 8,
                    borderRadius: '50%',
                    color: '#003366',
                    transition: 'color 200ms ease',
                    '&:hover': {
                        color: '#0055A4',
                        backgroundColor: 'rgba(0,51,102,0.04)',
                    },
                },
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    background: 'linear-gradient(135deg, #003366 0%, #0055A4 100%)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#002244',
                    color: '#FFFFFF',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                title: {
                    fontWeight: 600,
                    color: '#003366',
                },
                subheader: {
                    color: '#0055A4',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '16px 24px',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 8,
                        '& fieldset': {
                            borderColor: '#9e9e9e',
                        },
                        '&:hover fieldset': {
                            borderColor: '#0055A4',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#003366',
                            boxShadow: '0 0 6px rgba(0,51,102,0.2)',
                        },
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#003366',
                    color: '#FFFFFF',
                    fontSize: '0.875rem',
                },
                arrow: {
                    color: '#003366',
                },
            },
        },
    },
});

const theme = responsiveFontSizes(rawTheme);

export default theme;